import React, { useCallback, useEffect, useId, useRef, useState } from "react";

/**
 * マウスオーバーでカーソル位置に出てくるフローティングツールチップ。
 *
 * Fluent UI の TooltipHost と違い
 *  - カーソルが止まってから一定時間後に表示する
 *  - ツールチップ本体にカーソルを乗せている間は表示を維持する(中のリンクをクリックできる)
 *  - 中身に任意の JSX を置ける
 * といった性質が必要な箇所で使う。
 *
 * 使い方(リストの各行にツールチップを出す場合):
 *
 *   const tooltip = useFloatingToolTip<number>()
 *   ...
 *   {items.map(item => <div {...tooltip.getTargetProps(item.id)}>{item.name}</div>)}
 *   <FloatingToolTip controller={tooltip}>
 *     {(id) => <span>{descriptionOf(id)}</span>}
 *   </FloatingToolTip>
 *
 * キーが string / number 以外の場合は、識別用の文字列の作り方を keyOf で渡す:
 *
 *   const tooltip = useFloatingToolTip<DocRef>({
 *     keyOf: (doc) => `${doc.containerId}/${doc.fileName}`,
 *   })
 *
 * 単独の要素に付けるだけなら FloatingToolTipHost を使う。
 */

export interface UseFloatingToolTipOptions {
  /** カーソルが止まってから表示するまでの時間(ms) */
  showDelayMs?: number;
  /** カーソル位置の判定間隔(ms) */
  intervalMs?: number;
  /** true の間はツールチップを出さない */
  disabled?: boolean;
}

/**
 * キーから識別用の文字列を作る関数。
 * 対象ごとに一意な文字列を返すこと(この文字列が実質のハッシュキーになる)。
 * string / number をキーにする場合は省略でき、その場合は String() が使われる。
 */
export type ToolTipKeyOf<K> = (key: K) => string;

/** ツールチップを出したい要素に spread するプロパティ */
export interface FloatingToolTipTargetProps {
  "data-tooltip-scope": string;
  "data-tooltip-key": string;
}

export interface FloatingToolTipController<K> {
  /** 現在ツールチップを表示している対象のキー(非表示なら null) */
  activeKey: K | null;
  /** 表示を開始したカーソル位置 */
  position: { x: number; y: number };
  /** ツールチップを出したい要素に spread する */
  getTargetProps: (key: K) => FloatingToolTipTargetProps;
  /** FloatingToolTip が内部で使うツールチップ本体の ref */
  hostRef: React.RefObject<HTMLDivElement>;
  /** 強制的に非表示にする */
  hide: () => void;
}

const isCursorInside = (rect: DOMRect, cursor: { clientX: number; clientY: number }) =>
  cursor.clientX >= rect.left &&
  cursor.clientX <= rect.right &&
  cursor.clientY >= rect.top &&
  cursor.clientY <= rect.bottom;

/** キーが string / number の場合は keyOf を省略できる(String() が使われる) */
export function useFloatingToolTip<K extends string | number>(
  options?: UseFloatingToolTipOptions & { keyOf?: ToolTipKeyOf<K> }
): FloatingToolTipController<K>;
/** 任意の型をキーにする場合は keyOf で識別用の文字列の作り方を渡す */
export function useFloatingToolTip<K>(
  options: UseFloatingToolTipOptions & { keyOf: ToolTipKeyOf<K> }
): FloatingToolTipController<K>;
export function useFloatingToolTip<K>(
  options: UseFloatingToolTipOptions & { keyOf?: ToolTipKeyOf<K> } = {}
): FloatingToolTipController<K> {
  const { showDelayMs = 300, intervalMs = 100, disabled = false, keyOf } = options;
  // keyOf はインラインの関数で渡されることが多く毎レンダー別インスタンスになるため、
  // 監視ループを張り替えないように ref 経由で参照する
  const keyOfRef = useRef<ToolTipKeyOf<K>>(keyOf ?? String);
  keyOfRef.current = keyOf ?? String;
  // 同一ページに複数のツールチップがあっても対象を取り違えないためのスコープ
  const scope = useId();
  // keyString は data 属性に載せた keyOf(key)。同一性の判定はこちらで行う
  // (キーがオブジェクトの場合、レンダーごとに別インスタンスになりうるため参照比較はできない)
  const [state, setState] = useState<{ key: K | null; keyString: string; x: number; y: number }>({
    key: null,
    keyString: "",
    x: 0,
    y: 0,
  });
  const stateRef = useRef(state);
  stateRef.current = state;
  const hostRef = useRef<HTMLDivElement>(null);
  // data 属性は文字列になるので、元のキーに戻すための対応表
  const keyMap = useRef(new Map<string, K>());
  const mousePos = useRef({ clientX: 0, clientY: 0 });
  const mouseStopCounter = useRef(0);

  const hide = useCallback(() => setState({ key: null, keyString: "", x: 0, y: 0 }), []);

  const getTargetProps = useCallback(
    (key: K): FloatingToolTipTargetProps => {
      const keyString = keyOfRef.current(key);
      keyMap.current.set(keyString, key);
      return { "data-tooltip-scope": scope, "data-tooltip-key": keyString };
    },
    [scope]
  );

  // マウスの位置監視
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      mousePos.current = { clientX: e.clientX, clientY: e.clientY };
      mouseStopCounter.current = 0;
    };
    document.addEventListener("mousemove", handler);

    return () => {
      document.removeEventListener("mousemove", handler);
    };
  }, []);

  // 表示判定
  useEffect(() => {
    if (disabled) {
      if (stateRef.current.key !== null) hide();
      return;
    }
    const stopTicks = Math.max(1, Math.ceil(showDelayMs / intervalMs));

    const interval_id = setInterval(() => {
      const cursor = mousePos.current;
      const current = stateRef.current;

      // カーソルがどの対象の矩形領域に入っているか(data 属性に載っている文字列で識別する)
      let hoveredKeyString: string | null = null;
      const targets = document.querySelectorAll<HTMLElement>(`[data-tooltip-scope="${scope}"]`);
      for (const target of Array.from(targets)) {
        const keyString = target.dataset.tooltipKey;
        if (keyString === undefined) continue;
        if (isCursorInside(target.getBoundingClientRect(), cursor)) {
          hoveredKeyString = keyString;
          break;
        }
      }

      // ツールチップ本体の矩形領域にカーソルが入っているか
      const host = hostRef.current;
      const isCursorInsideOfToolTip =
        current.key !== null && host !== null && isCursorInside(host.getBoundingClientRect(), cursor);

      // 表示されている時
      if (current.key !== null) {
        // 両方の矩形領域に入っていない場合、表示しなくなる
        if (hoveredKeyString === null && !isCursorInsideOfToolTip) {
          hide();
        }
        // 対象には入っているがツールチップ本体には入っていない場合
        // 前回とは違う対象の矩形領域に入っているなら非表示にする(少し経ってから表示される)
        else if (hoveredKeyString !== null && !isCursorInsideOfToolTip && hoveredKeyString !== current.keyString) {
          hide();
        }
      }
      // 表示されていない時
      // 対象の矩形領域に入っているかつカーソルが止まってから一定の時間が経過していたら表示
      else if (hoveredKeyString !== null && stopTicks <= mouseStopCounter.current) {
        const hoveredKey = keyMap.current.get(hoveredKeyString);
        if (hoveredKey !== undefined) {
          setState({ key: hoveredKey, keyString: hoveredKeyString, x: cursor.clientX, y: cursor.clientY });
        }
      }

      // 監視変数更新
      mouseStopCounter.current += 1;
    }, intervalMs);

    return () => {
      clearInterval(interval_id);
    };
  }, [disabled, hide, intervalMs, scope, showDelayMs]);

  return { activeKey: state.key, position: { x: state.x, y: state.y }, getTargetProps, hostRef, hide };
}

export interface FloatingToolTipProps<K> {
  controller: FloatingToolTipController<K>;
  /** 表示内容。関数を渡すと表示中のキーを受け取れる */
  children?: React.ReactNode | ((key: K) => React.ReactNode);
  /** カーソル位置からのずらし量 */
  offset?: { x: number; y: number };
  className?: string;
  /** 既定のスタイルを上書きする */
  style?: React.CSSProperties;
}

/** useFloatingToolTip と対で使うツールチップ本体 */
export function FloatingToolTip<K>(props: FloatingToolTipProps<K>) {
  const { controller, children, offset, className, style } = props;
  const { activeKey, position, hostRef } = controller;
  const isShown = activeKey !== null;
  const x = position.x + (offset?.x ?? 0);
  const y = position.y + (offset?.y ?? 0);

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        position: "fixed",
        zIndex: 1000,
        left: x,
        top: y,
        background: "white",
        borderRadius: "5px",
        display: "block",
        overflow: "auto",
        transition: isShown ? "opacity 0.3s ease" : "opacity 0s",
        opacity: isShown ? 1 : 0,
        // 非表示中に透明な領域がクリックを奪わないようにする
        pointerEvents: isShown ? "auto" : "none",
        maxWidth: `calc(100vw - ${x}px)`,
        maxHeight: `calc(100vh - ${y}px)`,
        padding: "2px 5px",
        cursor: "default",
        ...style,
      }}
    >
      {activeKey !== null && (typeof children === "function" ? children(activeKey) : children)}
    </div>
  );
}

export interface FloatingToolTipHostProps extends UseFloatingToolTipOptions {
  /** ツールチップに表示する内容 */
  content: React.ReactNode;
  /** ホバー対象 */
  children: React.ReactNode;
  offset?: { x: number; y: number };
  /** ホバー対象を包む要素のスタイル */
  targetStyle?: React.CSSProperties;
  targetClassName?: string;
  toolTipStyle?: React.CSSProperties;
  toolTipClassName?: string;
}

/** 単独の要素にフローティングツールチップを付ける簡易版 */
export const FloatingToolTipHost = (props: FloatingToolTipHostProps) => {
  const {
    content,
    children,
    offset,
    targetStyle,
    targetClassName,
    toolTipStyle,
    toolTipClassName,
    ...toolTipOptions
  } = props;
  const controller = useFloatingToolTip<"target">(toolTipOptions);

  return (
    <>
      <div {...controller.getTargetProps("target")} className={targetClassName} style={targetStyle}>
        {children}
      </div>
      <FloatingToolTip controller={controller} offset={offset} className={toolTipClassName} style={toolTipStyle}>
        {content}
      </FloatingToolTip>
    </>
  );
};
